import React, { useRef, useState } from 'react';
import { Modal, Box, Typography, Button, Grid } from '@mui/material';
import BoxPlotChart from '../BoxPlotChart';
import PlotOptionsDialog from '../PlotOptionsDialog';
import { FileDownload, Settings } from '@mui/icons-material';
import SessionConstants from '../../config/SessionConstants';


const BoxplotModal = ({ 
  open, 
  onClose, 
  data, 
  title, 
  yAxisLabel, 
  xAxisLabel, 
  showZScore = false, 
  xAxisKey, 
  isPatientBiochemData = false,
  allStatistics = null,
  selectedAnalyte = null,
  programOptions = null,
  userProgramId = null,
  formatProgramId = null,
  roundNumericFields = null,
  patientData = null,
  onFetchStatistics = null,
  initialDataScope = null, // Default data scope to show in Plot Options
  testResultOptions = null, // Available test results for patient details
  initialTestResult = null, // Initial test result to show
  onFetchWithTestResult = null, // Callback to fetch data with specific test result
  minWidthPercent = 50, // Minimum dialog width (vw); grows with data up to 90
  constrainChartWidth = false // When true, cap the chart width to the data so a few plots don't stretch across a wide dialog
}) => {

  const chartRef = useRef();
  const [plotOptionsOpen, setPlotOptionsOpen] = useState(false);
  const [dataScope, setDataScope] = useState(initialDataScope); // Use prop for initial value
  const [testResult, setTestResult] = useState(initialTestResult); // Track selected test result
  const [originalData, setOriginalData] = useState(data); // Store original data from Submit
  const [filteredData, setFilteredData] = useState(data);
  const [isLoadingPlotOptions, setIsLoadingPlotOptions] = useState(false);
  const [titleSuffix, setTitleSuffix] = useState(''); // For appending to title based on data scope

  // Update originalData when data prop changes (from Submit button)
  React.useEffect(() => {
    setOriginalData(data);
  }, [data]);

  // Reset to original data when modal opens/closes
  React.useEffect(() => {
    if (open) {
      setDataScope(initialDataScope);
      setTestResult(initialTestResult);
      setFilteredData(originalData);
      setTitleSuffix(''); // Reset title suffix when modal opens
    }
  }, [open, originalData, initialDataScope, initialTestResult]);

  const handleSavePlotOptions = async (options) => {
    const { dataScope: newDataScope, testResult: newTestResult } = options;
    
    if (!onFetchStatistics && !onFetchWithTestResult) {
      return;
    }

    setIsLoadingPlotOptions(true);
    setDataScope(newDataScope);
    setTestResult(newTestResult);

    try {
      let newData;
      let suffix = '';
      
      // If test result changed and we have a handler for it
      if (newTestResult && onFetchWithTestResult && newTestResult.testId !== initialTestResult?.testId) {
        newData = await onFetchWithTestResult(newTestResult.testId, newDataScope);
        suffix = newDataScope === 'my_program' ? ` for Program ${userProgramId}` : ' for All Programs';
      } else if (newDataScope && onFetchStatistics) {
        // Only data scope changed
        let programsToFetch;
        
        if (newDataScope === 'my_program') {
          // Fetch only user's program
          programsToFetch = userProgramId;
          suffix = ` for Program ${userProgramId}`;
        } else if (newDataScope === 'all_programs') {
          // For PatientDetails: fetch ALL_PROGRAMS aggregate directly
          // For Explore: would need to join all programs, but that's handled differently
          programsToFetch = isPatientBiochemData ? 'ALL_PROGRAMS' : (programOptions?.join(',') || 'ALL_PROGRAMS');
          suffix = ' for All Programs';
        }

        // Call the parent's fetch function to get new statistics
        newData = await onFetchStatistics(programsToFetch);
      }
      
      if (newData) {
        setFilteredData(newData);
        setTitleSuffix(suffix);
      }
    } catch (error) {
      console.error('Failed to fetch plot options data', error);
      // On error, revert to original data
      setFilteredData(originalData);
      setDataScope(initialDataScope);
      setTestResult(initialTestResult);
      setTitleSuffix('');
    } finally {
      setIsLoadingPlotOptions(false);
    }
  };

  const handleExport = () => {
    chartRef.current?.exportAsPNG();
  };

  const handleExportEntire = () => {
    chartRef.current?.exportEntireAsPNG();
  };

  const handleOpenPlotOptions = () => {
    setPlotOptionsOpen(true);
  };

  const handleClosePlotOptions = () => {
    setPlotOptionsOpen(false);
  };

  // Size the dialog to the data: enough to show every plot, but never below the
  // caller's minimum (so small selections don't look tiny) nor above 90vw (past
  // which the chart scrolls horizontally). ~120px per plot plus chrome/padding.
  const plotCount = filteredData?.length || 0;
  const viewportPx = typeof window !== 'undefined' ? window.innerWidth : 1440;
  const neededPercent = ((plotCount * 120 + 212) / viewportPx) * 100;
  const dialogWidthPercent = Math.min(Math.max(neededPercent, minWidthPercent), 90);

  // Optionally cap the chart's own width to roughly the space its plots need
  // (~120px/plot plus axis + legend chrome), centered in the dialog. This keeps
  // one or two plots from floating in a sea of whitespace when the dialog is
  // held at its minimum width, while still letting many plots fill the space.
  // Floor at 2 plots' worth of width so a single boxplot isn't so narrow that
  // the axis label and legend text overlap.
  const chartMaxWidthPx = Math.max(plotCount, 2) * 120 + 260;

  return (
    <>
      <Modal open={open} onClose={onClose} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box
          role="dialog"
          aria-labelledby="boxplot-modal-title"
          sx={{
            minWidth: '40vw',
            maxWidth: '90vw',
            width: `${dialogWidthPercent}vw`,
            maxHeight: '90vh',
            minHeight: '40vh',
            bgcolor: 'background.paper',
            borderRadius: 2,
            p: 4,
            mx: 'auto',
            my: '10%',
            outline: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
        <Grid container alignItems="flex-start" spacing={2} sx={{ mb: 2 }}>
          {allStatistics && programOptions && (selectedAnalyte || (isPatientBiochemData && patientData)) ? (
            <Grid item xs={12} sm={2} textAlign="left">
              <Button
                variant="outlined"
                size="small"
                onClick={handleOpenPlotOptions}
                sx={{ ml: 1 }}
                startIcon={<Settings />}
              >
                Options
              </Button>
            </Grid>
          ) : (
            <Grid item xs={12} sm={2}>
              {/* Spacer when Options button is not available */}
            </Grid>
          )}
          <Grid item xs={12} sm={8} textAlign="center">
            <Typography id="boxplot-modal-title" variant="h6" noWrap={false}>
              {title}{titleSuffix}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={2} textAlign="right">
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleExport}
                startIcon={<FileDownload />}
              >
                Export View
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleExportEntire}
                startIcon={<FileDownload />}
              >
                Export Full
              </Button>
            </Box>
          </Grid>
        </Grid>          <Box
            sx={{
              width: '100%',
              ...(constrainChartWidth ? { maxWidth: `${chartMaxWidthPx}px`, mx: 'auto' } : {}),
            }}
          >
            <BoxPlotChart
              ref={chartRef}
              data={filteredData}
              showZScore={showZScore}
              yAxisLabel={yAxisLabel}
              xAxisLabel={xAxisLabel}
              exportTitle={title}
              xAxisKey={xAxisKey}
              isPatientBiochemData={isPatientBiochemData}
            />
          </Box>

          <Box mt={4} display="flex" justifyContent="center" width="100%">
            <Button variant="contained" onClick={onClose}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal>

      <PlotOptionsDialog
        open={plotOptionsOpen}
        onClose={handleClosePlotOptions}
        onSave={handleSavePlotOptions}
        currentDataScope={dataScope}
        isLoading={isLoadingPlotOptions}
        showTestResultSelector={isPatientBiochemData && testResultOptions && testResultOptions.length > 0}
        testResultOptions={testResultOptions}
        currentTestResult={testResult}
      />
    </>
  );
};

export default BoxplotModal;
